import { gql } from '@apollo/client'

import { PUBLIC_POST_FRAGMENT } from '../fragments'

/**
 * Graphql Query - To get all the active workspaces by the logged-in user
 * @param status: Active enum value
 */
export const MY_WORKSPACES = gql`
  query {
    myWorkspaces(input: { status: Active }) {
      accessType
      role
      status
      isPrimary
      workspace {
        displayName
        dot
        enabledApps {
          appMetadata {
            domainName
            onboardingURL
          }
          appType
        }
        enterpriseWorkspaceID
        id
        isEnterpriseWorkspace
        isVerified
        logo
        mcNumber
        shipmentsPageSettings {
          hideNeedsCarrierShipments
        }
        workspacename
        workspaceType
        workspaceVerificationType
      }
    }
  }
`

/**
 * Query to get the invited users of the workspace
 */
export const GET_WORKSPACE_INVITATIONS = gql`
  query WorkspaceInvitations($workspaceID: ID!) {
    workspaceInvitations(input: { workspaceID: $workspaceID }) {
      userID
      workspaceID
      accessType
      expirationDate
      user {
        id
        name
        photo
        email
        phone
        countryCode
      }
    }
  }
`

/**
 * Query to get the members of the workspace
 */
export const GET_PUBLIC_WORKSPACE_MEMBERS = gql`
  query {
    publicWorkspaceMembers {
      userID
      workspaceID
      accessType
      status
      user {
        id
        name
        photo
        email
        phone
        countryCode
      }
    }
  }
`

/**
 * This query returns the company by DOT or MC
 */
export const GET_COMPANY_BY_DOT_OR_MC = gql`
  query Company($id: ID!, $type: String!) {
    company(input: { id: $id, type: $type }) {
      id
      mcNumber
      companyName
      allowedToOperate
      authorizedForBroker
      authorizedForProperty
      brokerAuthorityStatus
      commonAuthorityStatus
      contractAuthorityStatus
      physicalAddress {
        street
        city
        state
      }
    }
  }
`

export const GET_CONSTRAINT = gql`
  query GetConstraint($constraint: String!) {
    getConstraint(input: { constraint: $constraint }) {
      id
      uuid
      type
      workspace {
        id
        logo
        displayName
        isVerified
        workspacename
        workspaceType
        dot
        mcNumber
        workspacePage {
          bio
          freightPreferences
        }
        isEnterpriseWorkspace
        enterpriseWorkspaceID
      }
      canEditPage
    }
  }
`

/**
 * This query returns the workspace with restricted data by DOT or MC
 */
export const GET_WORKSPACE_BY_DOT_OR_MC = gql`
  query GetWorkspaceByDOTOrMCNumber($dotOrMCNumber: String!, $type: DOTOrMCType!) {
    getWorkspaceByDOTOrMCNumber(input: { dotOrMCNumber: $dotOrMCNumber, type: $type }) {
      id
      displayName
      dot
      mcNumber
      workspaceType
      workspacename
      logo
    }
  }
`

export const GET_COMPANY_VERIFICATION_METHODS = gql`
  query CompanyVerificationMethods($companyIdentifier: CompanyIdentifierInput!, $workspaceID: ID!) {
    companyVerificationMethods(input: { companyIdentifier: $companyIdentifier, workspaceID: $workspaceID }) {
      verificationMethods {
        value
        type
      }
    }
  }
`

export const GET_WORKSPACE = gql`
  query {
    workspace: getWorkspaceByID {
      displayName
      id
      logo
      workspacename
    }
  }
`

export const GET_WORKSPACE_SIGNED_URL = gql`
  query GetWorkspaceSignedURL($imageType: String!) {
    workspace: getWorkspaceLogoSignedURL(input: { imageType: $imageType }) {
      url
    }
  }
`

/**
 * Get all workspaces associated with an IP
 */
export const GET_IP_WORKSPACE_ASSOCIATIONS = gql`
  query IPWorkspaceAssociations($ip: ID!) {
    ipWorkspaceAssociations(input: { ip: $ip }) {
      id
      ip
      isAbuser
      isBogon
      isCrawler
      isDatacenter
      isProxy
      isTOR
      isVPN
      lastUsed
      location {
        city
        country
        countryCode
        region
        state
        latitude
        longitude
        zip
      }
      workspace {
        id
        displayName
        isVerified
        logo
        dot
        mcNumber
      }
    }
  }
`

export const SEARCH_WORKSPACE_POSTS = gql`
  query Search($workspaceID: ID!, $loadNumber: String) {
    search(input: { workspaceID: $workspaceID, loadNumber: $loadNumber }) {
      ...PostFields
      created
    }
  }
  ${PUBLIC_POST_FRAGMENT}
`

export const GET_WORKSPACE_API_KEYS = gql`
  query {
    workspaceAPIKeys {
      apiKey
      workspaceID
      scopes
      created
      modified
    }
  }
`
